import { useQuery } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'

export interface AttestationDocument {
  url: string
  name: string
  document_type?: string
}

export interface AttestationTemplate {
  attestation_type: string
  version: number
  rendered_text: string
  documents?: AttestationDocument[]
}

export interface AttestationConsent {
  attestation_type: string
  version: number
  content_hash: string
  bool_value: boolean
  attested_at: string
  documents_opened_at?: Record<string, string>
}

/** Which surface the templates are for — the review step, or a carrier's quote card. */
export enum AttestationScope {
  REVIEW = 'review',
  CARRIER = 'carrier',
}

export async function fetchAttestations(
  orgId: string,
  scope?: AttestationScope
): Promise<AttestationTemplate[]> {
  const response = await axios.get(`/api/v1/insure/organisations/${orgId}/quotes/attestations/`, {
    params: scope ? { scope } : undefined,
  })
  return response.data
}

export async function acceptAttestations(orgId: string, attestations: AttestationConsent[]): Promise<void> {
  await axios.post(`/api/v1/insure/organisations/${orgId}/quotes/accept-attestations/`, { attestations })
}

const ATTESTATION_REQUIRED_CODE = 'ATTESTATION_REQUIRED'

/** Checkout, request-to-bind and quote picks answer 409 with this until the customer confirms their information. */
export function isAttestationRequiredError(error: unknown): boolean {
  if (!axios.isAxiosError(error) || error.response?.status !== 409) return false
  return error.response.data?.code === ATTESTATION_REQUIRED_CODE
}

export async function fetchBindAttestations(orgId: string, quoteId: string): Promise<AttestationTemplate[]> {
  const response = await axios.get(
    `/api/v1/insure/organisations/${orgId}/quotes/${quoteId}/bind-attestations/`
  )
  return response.data
}

/**
 * Carrier terms outstanding for this org, fetched up front.
 *
 * A card should only offer to collect terms it can actually show: the templates come
 * from the carrier's API, so an outage would otherwise leave the customer clicking
 * Review and getting an empty dialog.
 */
export function useCarrierAttestations(orgId?: string) {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.CARRIER_ATTESTATIONS, orgId],
    queryFn: () => fetchAttestations(orgId!, AttestationScope.CARRIER),
    enabled: !!orgId,
  })
  return { templates: data ?? [], isLoading }
}
